/**
 * NotificationsBell — the in-app notifications bell (v2.2 Phase 18).
 *
 * A bell button with an unread badge; clicking opens a dropdown of recent notifications. Clicking an
 * unread item marks it read; "Mark all read" clears the badge. Light-polls in the background.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type AppNotification,
} from "../api/notifications";

function timeAgo(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale);
}

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const notifsQ = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = notifsQ.data?.items ?? [];
  const unread = notifsQ.data?.unread ?? 0;

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onItemClick = (n: AppNotification) => {
    if (!n.read) void markRead.mutateAsync(n.id);
  };

  return (
    <div className="notif" ref={ref}>
      <button
        type="button"
        className="notif__bell"
        aria-label={t("notifications.title")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span className="notif__badge" aria-label={t("notifications.unread", { count: unread })}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif__panel" role="menu">
          <div className="notif__head">
            <strong>{t("notifications.title")}</strong>
            {unread > 0 && (
              <button
                type="button"
                className="notif__markall"
                disabled={markAll.isPending}
                onClick={() => void markAll.mutateAsync()}
              >
                {t("notifications.markAll")}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="notif__empty faint">{t("notifications.empty")}</p>
          ) : (
            <ul className="notif__list">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`notif__item ${n.read ? "" : "notif__item--unread"}`.trim()}
                  onClick={() => onItemClick(n)}
                >
                  <span className="notif__item-title">{n.title}</span>
                  <span className="notif__item-body faint">{n.body}</span>
                  <span className="notif__item-time faint">
                    {timeAgo(n.created_at, i18n.language)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
